import {useState, useRef, useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useConfig} from '../contexts/ClientConfigContext';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Portal from '@mui/material/Portal';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronRight, faListCheck, faSquarePollHorizontal} from '@fortawesome/free-solid-svg-icons';

const APP_ICONS = {
    faQuiz: faListCheck,
    faFactCheck: faSquarePollHorizontal,
};

const AppSwitcher = ({currentApp, expanded = true}) => {
    const config = useConfig();
    const apps = config.apps || [];
    const navigate = useNavigate();
    const location = useLocation();

    const [open, setOpen] = useState(false);
    const [panelPos, setPanelPos] = useState({top: 0, left: 0});
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                panelRef.current && !panelRef.current.contains(e.target)
            ) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const handleOpen = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPanelPos({
                top: rect.bottom + 8,
                left: rect.left,
            });
        }
        setOpen(v => !v);
    };

    if (!apps.length) return null;

    const isExternal = (url) => url?.startsWith('http');
    const isActive = (app) => {
        if (!app.url) return false;
        if (isExternal(app.url)) return false;
        return location.pathname.startsWith(app.url);
    };

    return (
        <>
            <Box
                ref={triggerRef}
                onClick={handleOpen}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    cursor: 'pointer', width: '100%',
                }}
            >
                <Box sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    backgroundColor: '#3f3d8f',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    {currentApp?.icon && APP_ICONS[currentApp.icon] && (
                        <FontAwesomeIcon icon={APP_ICONS[currentApp.icon]} style={{color: '#fff', fontSize: 16}}/>
                    )}
                </Box>

                {expanded && (
                    <>
                        <Typography variant="subtitle2" fontWeight={700} sx={{flexGrow: 1}}>
                            {currentApp?.label || config.branding.appName}
                        </Typography>
                        <Box sx={{
                            width: 20, height: 20, borderRadius: '50%',
                            border: '1.5px solid #1A237E',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <FontAwesomeIcon icon={faChevronRight} style={{fontSize: 10, color: '#1A237E'}}/>
                        </Box>
                    </>
                )}
            </Box>

            {open && (
                <Portal>
                    <Box
                        ref={panelRef}
                        sx={{
                            position: 'fixed',
                            top: panelPos.top,
                            left: panelPos.left,
                            backgroundColor: '#fff',
                            borderRadius: 3,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                            p: 2.5,
                            zIndex: 1400,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={700} sx={{mb: 2}}>
                            Mes applications Quadral
                        </Typography>

                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5}}>
                            {apps.map(app => {
                                const active = isActive(app);
                                const icon = APP_ICONS[app.icon];
                                return (
                                    <Box
                                        key={app.id}
                                        onClick={() => {
                                            if (!active && app.url) {
                                                isExternal(app.url)
                                                    ? window.location.href = app.url
                                                    : navigate(app.url);
                                            }
                                            setOpen(false);
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 1.5,
                                            borderRadius: 2,
                                            cursor: active ? 'default' : 'pointer',
                                            opacity: active ? 0.55 : 1,
                                            transition: 'background 0.15s, opacity 0.15s',
                                            '&:hover': {
                                                backgroundColor: active ? '#f0f0f0' : '#cfd5ff',
                                            },
                                        }}
                                    >
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: 1.5,
                                            backgroundColor: '#3f3d8f',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                            opacity: active ? 0.5 : 1
                                        }}>
                                            {icon && (
                                                <FontAwesomeIcon icon={icon} style={{color: '#fff', fontSize: 15}}/>
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            fontWeight={active ? 600 : 400}
                                            color={active ? '#3f3d8f' : '#333'}
                                            sx={{lineHeight: 1.3, fontSize: 13}}
                                        >
                                            {app.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Portal>
            )}
        </>
    );
};

export default AppSwitcher;